// Reads which Auth0 organisation an access token acts in, and which API it was issued for, so the
// portal never sends a token minted for one account while the UI shows another, or a token for one
// API to another.
//
// auth0-spa-js caches tokens per client, audience and scope only (not per organisation) and keeps
// refresh tokens bound to the organisation they were issued in. A cached or refreshed token can
// therefore belong to a previous organisation. The claims are read without verifying the
// signature: the API still verifies the token; this check only refuses tokens the portal must not
// send. Anything that cannot be read is treated as a mismatch (fail closed).

export type AccessTokenOrganization =
  | { readable: true; organization?: string }
  | { readable: false };

const BASE64URL_SEGMENT = /^[A-Za-z0-9_-]+$/;

const decodeBase64UrlJson = (segment: string): unknown => {
  if (!BASE64URL_SEGMENT.test(segment)) return undefined;

  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

  try {
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch {
    return undefined;
  }
};

const isClaimSet = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readAccessTokenClaims = (accessToken: unknown): Record<string, unknown> | undefined => {
  if (typeof accessToken !== 'string') return undefined;

  const segments = accessToken.split('.');
  if (segments.length !== 3) return undefined;

  const claims = decodeBase64UrlJson(segments[1]);
  return isClaimSet(claims) ? claims : undefined;
};

export const readAccessTokenOrganization = (accessToken: unknown): AccessTokenOrganization => {
  const claims = readAccessTokenClaims(accessToken);
  if (!claims) return { readable: false };

  const organization = claims.org_id;
  if (organization === undefined) return { readable: true };
  if (typeof organization !== 'string' || !organization.trim()) return { readable: false };

  return { readable: true, organization: organization.trim() };
};

// True only when the token is readable and acts in exactly the given organisation; an
// organisation-less token matches only an organisation-less account and vice versa.
export const isAccessTokenForOrganization = (accessToken: unknown, organization: string | undefined): boolean => {
  const tokenOrganization = readAccessTokenOrganization(accessToken);

  return tokenOrganization.readable && tokenOrganization.organization === organization;
};

// True only when the token is readable and was issued for the given API. Auth0 sends `aud` as a
// string, or as an array that also names the tenant's userinfo endpoint when `openid` was asked for.
export const isAccessTokenForAudience = (accessToken: unknown, audience: string): boolean => {
  const tokenAudience = readAccessTokenClaims(accessToken)?.aud;

  return typeof tokenAudience === 'string'
    ? tokenAudience === audience
    : Array.isArray(tokenAudience) && tokenAudience.includes(audience);
};

export class AccountMismatchError extends Error {
  constructor() {
    super('The access token belongs to a different organisation than the signed-in account. It was not used.');
    this.name = 'AccountMismatchError';
  }
}

export class AccessTokenAudienceMismatchError extends Error {
  constructor() {
    super('The access token was issued for a different API than the one requested. It was not used.');
    this.name = 'AccessTokenAudienceMismatchError';
  }
}
